import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Wallet,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';

interface Payment {
  id: number;
  employee_ref: string | null;
  beneficiary_name: string;
  account_number: string;
  branch_code: string;
  bank_name: string | null;
  amount: string;
  reference: string | null;
  status: string;
  rejection_code: string | null;
  rejection_reason: string | null;
  processed_at: string | null;
  fee_cents: number;
  payment_rail: string;
}

interface OutcomeGroup {
  outcome: string;
  count: number;
  items: Array<{
    id: number;
    name: string;
    msisdn: string;
    email: string | null;
    amount: number;
    status: string;
    inviteDeadlineAt?: string | null;
    suggestedMatch?: string | null;
  }>;
}

interface RunDetail {
  id: number;
  run_reference: string;
  rail: string;
  pay_period: string | null;
  total_amount: string;
  total_count: number;
  success_count: number;
  failed_count: number;
  pending_count: number;
  status: string;
  maker_user_id: number;
  checker_user_id: number | null;
  submitted_at: string | null;
  completed_at: string | null;
  created_at: string;
  payments: Payment[];
  outcomeSummary?: OutcomeGroup[];
  metadata?: { funding_mode?: string; estimated_grand_total_cents?: number };
  disbursementFundingMode?: 'prefunded_float' | 'deposit_per_batch';
}

interface FundingState {
  fundingStatus: string | null;
  fundedAt: string | null;
  depositAmountCents: number | null;
  runTotalCents: number;
  shortByCents: number | null;
}

const formatZAR = (n: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(n);

const formatDate = (iso: string | null) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-slate-50 text-slate-700 border-slate-200',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_funding: 'bg-sky-50 text-sky-700 border-sky-200',
  funded: 'bg-teal-50 text-teal-700 border-teal-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  submitted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  processing: 'bg-purple-50 text-purple-700 border-purple-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-orange-50 text-orange-700 border-orange-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  pending_registration: 'bg-sky-50 text-sky-700 border-sky-200',
  pending_typo_review: 'bg-amber-50 text-amber-800 border-amber-200',
  reversed: 'bg-slate-50 text-slate-700 border-slate-200',
};

function statusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const PaymentStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  const cls = 'h-4 w-4 shrink-0';
  switch (status) {
    case 'success':
    case 'accepted':
      return <CheckCircle className={`${cls} text-emerald-500`} />;
    case 'failed':
    case 'rejected':
      return <XCircle className={`${cls} text-red-500`} />;
    default:
      return <Clock className={`${cls} text-amber-500`} />;
  }
};

export const ClientRunDetailOverlay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { apiBase, authHeaders, cpPath, user } = useCpPortal();
  const [run, setRun] = useState<RunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [funding, setFunding] = useState<FundingState | null>(null);
  const [clientFundingMode, setClientFundingMode] = useState<'prefunded_float' | 'deposit_per_batch'>('prefunded_float');

  const fetchRun = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();

    try {
      const res = await fetch(`${apiBase}/runs/${id}`, { headers });
      const data = await res.json();
      if (data.success) {
        setRun(data.data);
        if (data.data?.disbursementFundingMode) {
          setClientFundingMode(data.data.disbursementFundingMode);
        }
        fetch(`${apiBase}/runs/${id}/funding`, { headers })
          .then((r) => r.json())
          .then((fd) => { if (fd.success) setFunding(fd.data); })
          .catch(() => {});
      } else {
        setError(data.error || 'Failed to load run');
      }
    } catch (err) {
      setError('Failed to load run details');
    } finally {
      setLoading(false);
    }
  }, [id, apiBase, authHeaders]);

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  const handleSubmitForApproval = async () => {
    if (!run) return;
    setSubmitting(true);
    setError('');

    const headers = { ...authHeaders(), 'Content-Type': 'application/json' };
    try {
      const res = await fetch(`${apiBase}/runs/${run.id}/submit`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (data.success) {
        await fetchRun();
      } else {
        setError(data.error || 'Failed to submit');
      }
    } catch {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!run) return;
    setSubmitting(true);
    setError('');
    const headers = { ...authHeaders(), 'Content-Type': 'application/json' };
    try {
      const res = await fetch(`${apiBase}/runs/${run.id}/approve`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (data.success) {
        await fetchRun();
      } else {
        setError(data.error || 'Failed to approve');
      }
    } catch {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!run) return;
    const reason = window.prompt('Reason for rejecting this run?');
    if (reason === null) return;
    setSubmitting(true);
    setError('');
    const headers = { ...authHeaders(), 'Content-Type': 'application/json' };
    try {
      const res = await fetch(`${apiBase}/runs/${run.id}/reject`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (data.success) {
        await fetchRun();
      } else {
        setError(data.error || 'Failed to reject');
      }
    } catch {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadResults = async () => {
    if (!run) return;
    try {
      const res = await fetch(`${apiBase}/runs/${run.id}/results`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        setError('Failed to download results');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${run.run_reference}_results.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="flex h-64 flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-3 h-10 w-10 text-amber-500" />
        <p className="text-sm text-[var(--foreground)]">{error || 'Run not found'}</p>
        <button
          type="button"
          onClick={() => navigate(cpPath('/runs'))}
          className="mt-4 text-sm font-medium text-[var(--primary)] hover:underline"
        >
          Back to runs
        </button>
      </div>
    );
  }

  // Maker submit (legacy draft path). Hidden for deposit-gated runs.
  const canSubmit = run.status === 'draft' && (user?.role === 'maker' || user?.role === 'admin');
  const runFundingMode =
    run.metadata?.funding_mode || run.disbursementFundingMode || clientFundingMode;
  const isPrefundedRun = runFundingMode === 'prefunded_float';
  const isChecker = user?.role === 'checker' || user?.role === 'admin';
  const isOwnRun = user?.id != null && Number(run.maker_user_id) === Number(user.id);
  // pending_approval = prefunded float path (or legacy draft submit). funded = deposit-per-batch gate.
  const canApprove = isChecker && !isOwnRun && (
    run.status === 'pending_approval' ||
    (run.status === 'funded' && !isPrefundedRun)
  );
  const canReject = ['pending_funding', 'funded', 'pending_approval'].includes(run.status) && isChecker && !isOwnRun;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(cpPath('/runs'))}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
            aria-label="Back to runs"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-[var(--foreground)]">{run.run_reference}</h2>
            <span
              className={`mt-1 inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                STATUS_STYLES[run.status] || 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
            >
              {statusLabel(run.status)}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchRun}
            className="flex min-h-[36px] items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>

          {canSubmit && (
            <button
              type="button"
              onClick={handleSubmitForApproval}
              disabled={submitting}
              className="flex min-h-[36px] items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              <Send className="h-4 w-4" aria-hidden />
              {submitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          )}

          {canApprove && (
            <button
              type="button"
              onClick={handleApprove}
              disabled={submitting}
              className="flex min-h-[36px] items-center gap-2 rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              <ThumbsUp className="h-4 w-4" aria-hidden />
              {submitting ? 'Approving...' : 'Approve & Disburse'}
            </button>
          )}

          {canReject && (
            <button
              type="button"
              onClick={handleReject}
              disabled={submitting}
              className="flex min-h-[36px] items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-60"
            >
              <ThumbsDown className="h-4 w-4" aria-hidden />
              Reject
            </button>
          )}

          {run.payments && run.payments.length > 0 && (
            <button
              type="button"
              onClick={handleDownloadResults}
              className="flex min-h-[36px] items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-4 py-1.5 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--muted)]"
            >
              <Download className="h-4 w-4" aria-hidden />
              Download CSV
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--destructive)]/20 bg-[var(--destructive)]/10 px-4 py-3 text-sm text-[var(--destructive)]">
          {error}
        </div>
      )}

      {/* Funding status banner */}
      {funding && !isPrefundedRun && (run.status === 'pending_funding' || run.status === 'funded' || run.status === 'underfunded') && (
        <div
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
            funding.fundingStatus === 'funded'
              ? 'border-teal-200 bg-teal-50 text-teal-800'
              : funding.fundingStatus === 'underfunded'
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-sky-200 bg-sky-50 text-sky-800'
          }`}
        >
          <Wallet className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <div className="flex-1">
            {funding.fundingStatus === 'funded' && (
              <span>
                Deposit received {funding.fundedAt ? `on ${formatDate(funding.fundedAt)}` : ''} —
                this run is funded and ready for checker approval.
              </span>
            )}
            {funding.fundingStatus === 'underfunded' && (
              <span>
                Deposit of {formatZAR((funding.depositAmountCents || 0) / 100)} received but
                short by {formatZAR((funding.shortByCents || 0) / 100)}. A supplementary deposit
                quoting your deposit reference is required before approval.
              </span>
            )}
            {funding.fundingStatus === 'pending_funding' && (
              <span>
                Awaiting deposit of {formatZAR(funding.runTotalCents / 100)}. Quote your deposit
                reference when funding so the run is matched automatically.
              </span>
            )}
          </div>
        </div>
      )}

      {isPrefundedRun && run.status === 'pending_approval' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Wallet className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <span>
            Reserved from your prefunded float — awaiting checker approval before payments are sent.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
          <p className="text-xs text-[var(--muted-foreground)]">Total Amount</p>
          <p className="mt-1 text-lg font-bold text-[var(--foreground)]">
            {formatZAR(parseFloat(run.total_amount || '0'))}
          </p>
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
          <p className="text-xs text-[var(--muted-foreground)]">Payments</p>
          <p className="mt-1 text-lg font-bold text-[var(--foreground)]">{run.total_count}</p>
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
          <p className="text-xs text-[var(--muted-foreground)]">Type</p>
          <p className="mt-1 text-lg font-bold text-[var(--foreground)]">MyMoolah Wallet</p>
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
          <p className="text-xs text-[var(--muted-foreground)]">Created</p>
          <p className="mt-1 text-sm font-medium text-[var(--foreground)]">{formatDate(run.created_at)}</p>
        </div>
      </div>

      {/* Progress bar */}
      {run.total_count > 0 && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5">
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="font-medium text-[var(--foreground)]">Payment Progress</span>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                Success: {run.success_count}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                Failed: {run.failed_count}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                Pending: {run.pending_count}
              </span>
            </div>
          </div>
          <div className="flex h-3 overflow-hidden rounded-full bg-[var(--muted)]">
            {run.success_count > 0 && (
              <div
                className="bg-emerald-500 transition-all"
                style={{ width: `${(run.success_count / run.total_count) * 100}%` }}
              />
            )}
            {run.failed_count > 0 && (
              <div
                className="bg-red-500 transition-all"
                style={{ width: `${(run.failed_count / run.total_count) * 100}%` }}
              />
            )}
            {run.pending_count > 0 && (
              <div
                className="bg-amber-400 transition-all"
                style={{ width: `${(run.pending_count / run.total_count) * 100}%` }}
              />
            )}
          </div>
        </div>
      )}

      {run.outcomeSummary && run.outcomeSummary.length > 0 && (
        <div className="space-y-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-5">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">Run outcomes</h3>
          {run.outcomeSummary.map((group) => (
            <div key={group.outcome} className="rounded-lg border border-[var(--border)] p-3">
              <p className="text-sm font-medium text-[var(--foreground)]">
                {group.count} {group.outcome}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-[var(--muted-foreground)]">
                {group.items.slice(0, 8).map((item) => (
                  <li key={item.id}>
                    {item.name} — {item.msisdn} — {formatZAR(item.amount)}
                    {item.suggestedMatch ? ` (suggested: ${item.suggestedMatch})` : ''}
                  </li>
                ))}
                {group.items.length > 8 && (
                  <li>…and {group.items.length - 8} more (see CSV download)</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Payments table */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <div className="border-b border-[var(--border)] px-5 py-4">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">
            Payments ({run.payments?.length || 0})
          </h3>
        </div>

        {(!run.payments || run.payments.length === 0) ? (
          <div className="py-12 text-center text-sm text-[var(--muted-foreground)]">
            No payments in this run
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/30">
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Status</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Beneficiary</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Mobile</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Email</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)] text-right">Amount</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Reference</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Deadline</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Rejection</th>
                </tr>
              </thead>
              <tbody>
                {run.payments.map((p) => (
                  <tr key={p.id} className="border-b border-[var(--border)] last:border-0">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <PaymentStatusIcon status={p.status} />
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[p.status] || 'bg-gray-50 text-gray-600 border-gray-200'
                          }`}
                        >
                          {statusLabel(p.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[var(--foreground)]">{p.beneficiary_name}</td>
                    <td className="px-5 py-3 font-mono text-xs text-[var(--muted-foreground)]">
                      {p.account_number}
                    </td>
                    <td className="px-5 py-3 text-xs text-[var(--muted-foreground)]">
                      {(p as Payment & { recipient_email?: string }).recipient_email || '-'}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-[var(--foreground)]">
                      {formatZAR(parseFloat(p.amount || '0'))}
                    </td>
                    <td className="px-5 py-3 text-xs text-[var(--muted-foreground)]">
                      {p.reference || '-'}
                    </td>
                    <td className="px-5 py-3 text-xs text-[var(--muted-foreground)]">
                      {p.outcome === 'Credited — KYC required'
                        ? 'No expiry — KYC to unlock'
                        : p.status === 'pending_registration' && (p as Payment & { invite_deadline_at?: string }).invite_deadline_at
                          ? formatDate((p as Payment & { invite_deadline_at?: string }).invite_deadline_at!)
                          : '-'}
                    </td>
                    <td className="max-w-[200px] px-5 py-3 text-xs text-red-600">
                      {p.rejection_reason ? (
                        <span title={p.rejection_reason}>
                          {p.rejection_code ? `[${p.rejection_code}] ` : ''}
                          {p.rejection_reason.length > 50
                            ? p.rejection_reason.substring(0, 50) + '...'
                            : p.rejection_reason}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
